import React, { useState } from 'react';
import { Form, Select, Button, InputNumber } from 'antd';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { getLabServiceOptions, getLabServiceCategories, getPricing } from '@app/api/nominalPayments.api';
import { notificationController } from '@app/controllers/notificationController';

const { Option } = Select;

interface LabItemFormProps {
  onAdd: (item: any) => void;
  divisionOptions: { label: string; value: string }[];
}

const LabItemForm: React.FC<LabItemFormProps> = ({ onAdd, divisionOptions }) => {
  const [form] = Form.useForm();
  const [selectedDivision, setSelectedDivision] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [serviceCategoryOptions, setServiceCategoryOptions] = useState<{ value: number; label: string }[]>([]);
  const [serviceOptions, setServiceOptions] = useState<{ value: number; label: string }[]>([]);

  const fetchServiceCategories = async (type: string, divId: number) => {
    try {
      const res = await getLabServiceCategories(type, divId);
      setServiceCategoryOptions(res.lab_service_category_options || []);
      setServiceOptions([]);
      form.setFieldsValue({ lab_service_category_id: undefined, lab_service_id: undefined });
    } catch {
      setServiceCategoryOptions([]);
    }
  };

  const fetchServices = async (type: string, catId: number) => {
    try {
      const res = await getLabServiceOptions(type, catId);
      setServiceOptions(res.lab_service_options || []);
      form.setFieldsValue({ lab_service_id: undefined });
    } catch {
      setServiceOptions([]);
    }
  };

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();

      const allFormValues = form.getFieldsValue();

      const pricingData = await getPricing(
        selectedType,
        null,
        null,
        null,
        parseInt(values.department_id),
        null,
        values.qty,
        'LAB',
        values.lab_service_id,
        null,
      );

      onAdd({
        ...values,
        key: `item-${Date.now()}`,
        divisionLabel: allFormValues.divisionLabel,
        typeLabel: allFormValues.typeLabel,
        mainServiceLabel: allFormValues.mainServiceLabel,
        subServiceLabel: allFormValues.subServiceLabel,
        uom: pricingData.data.uom || 'Nos',
        unit_price: Number(pricingData.data.unit_price),
        qty: Number(values.qty || 1),
        total: Number(pricingData.data.unit_price) * Number(values.qty || 1),
      });

      form.resetFields();
      setSelectedDivision('');
      setSelectedType('');
      setServiceCategoryOptions([]);
      setServiceOptions([]);
    } catch (err) {
      console.error('Error in handleAdd:', err);
      notificationController.error({
        message: 'Could not add item to the Table',
        description: 'Failed to add item. Please check the form fields and try again.',
      });
    }
  };

  return (
    <Form
      form={form}
      name="labNewTaskForm"
      size="small"
      labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
      labelAlign="left"
      wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
      initialValues={{ qty: 1 }}
    >
      <BaseRow gutter={[16, 16]}>
        <BaseCol xs={24} md={8} lg={12}>
          <Form.Item name="department_id" label="Division" rules={[{ required: true }]}>
            <Select
              placeholder="Select Division"
              onChange={(value, option: any) => {
                setSelectedDivision(value);
                form.setFieldsValue({
                  department_id: value,
                  divisionLabel: option?.label,
                });
                if (selectedType) fetchServiceCategories(selectedType, parseInt(value));
                form.setFieldsValue({ type: undefined, lab_service_category_id: undefined, lab_service_id: undefined });
              }}
            >
              {divisionOptions.map((div) => (
                <Option key={div.value} value={div.value} label={div.label}>
                  {div.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </BaseCol>

        <BaseCol xs={24} md={8} lg={12}>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Select
              placeholder="Select Type"
              onChange={(value, option: any) => {
                setSelectedType(value);
                form.setFieldsValue({
                  type: value,
                  typeLabel: option?.label,
                });
                if (selectedDivision) fetchServiceCategories(value, parseInt(selectedDivision));
                form.setFieldsValue({ lab_service_category_id: undefined, lab_service_id: undefined });
              }}
            >
              <Option value="TEST" label="Test">
                Test
              </Option>
              <Option value="VACCINE" label="Vaccine">
                Vaccine
              </Option>
              <Option value="PRODUCT" label="Product">
                Product
              </Option>
            </Select>
          </Form.Item>
        </BaseCol>

        <BaseCol xs={24} md={8} lg={12}>
          <Form.Item name="lab_service_category_id" label="Service Category" rules={[{ required: true }]}>
            <Select
              placeholder="Select Service Category"
              onChange={(value, option: any) => {
                form.setFieldsValue({
                  lab_service_category_id: value,
                  mainServiceLabel: option?.label,
                });

                const typeValue = form.getFieldValue('type');
                fetchServices(typeValue, value);
              }}
            >
              {serviceCategoryOptions.map((opt) => (
                <Option key={opt.value} value={opt.value} label={opt.label}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </BaseCol>

        <BaseCol xs={24} md={8} lg={12}>
          <Form.Item name="lab_service_id" label="Service" rules={[{ required: true }]}>
            <Select
              placeholder="Select Service"
              onChange={(value, option: any) => {
                form.setFieldsValue({
                  lab_service_id: value,
                  subServiceLabel: option?.label,
                });
              }}
            >
              {serviceOptions.map((opt) => (
                <Option key={opt.value} value={opt.value} label={opt.label}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </BaseCol>
      </BaseRow>

      <BaseRow>
        <BaseCol xs={24} md={8} lg={12}>
          <Form.Item name="qty" label="Quantity" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </BaseCol>
      </BaseRow>

      <Form.Item name="divisionLabel" hidden />
      <Form.Item name="typeLabel" hidden />
      <Form.Item name="mainServiceLabel" hidden />
      <Form.Item name="subServiceLabel" hidden />

      <Button type="primary" size="small" onClick={handleAdd}>
        Add to bill
      </Button>
    </Form>
  );
};

export default LabItemForm;
